import {
  getSessionUuid,
  getTokenLocalStorage,
  getTokenSessionStorage,
  setTokenLocalStorage,
  setTokenSessionStorage
} from '@config/storage';
import { RootState, store } from '@redux/store';
import { GraphQLClient, RequestMiddleware, ResponseMiddleware } from 'graphql-request';

const responseMiddleware: ResponseMiddleware = async (response: any) => {
  // Debug logging for page refresh investigation (using warn to work in production)
  if (response?.response?.errors) {
    console.warn('[GraphQL Error]', response?.response?.errors);
  }

  const refreshed = response?.headers?.get('Refreshed');
  if (refreshed) {
    const state: RootState = store.getState();
    const rememberMe = state.user?.rememberMe;
    if (rememberMe) {
      setTokenLocalStorage(refreshed);
    } else {
      setTokenSessionStorage(refreshed);
    }
  }

  // Check for seat access error
  if (
    response?.response?.errors &&
    response?.response?.errors[0]?.message &&
    response?.response?.errors[0]?.message.includes('No active seat assigned')
  ) {
    console.warn('[GraphQL] Redirecting to logout - No active seat');
    // Redirect to logout page with seat error message
    window.location.href = '/logout?noSeat=true';
    return;
  }

  // Bad token / user / password
  if (
    response?.response?.errors &&
    ['Forbidden resource', 'NOT AUTHORIZED'].includes(response?.response?.errors[0]?.message)
  ) {
    console.warn('[GraphQL] Redirecting to logout - Forbidden/Not authorized');
    //Redirect to process logout and then to start page
    window.location.href = '/logout';
  }

  // Attempting to use same user across different login sessions
  if (
    response?.response?.errors &&
    response?.response?.errors[0]?.message &&
    response?.response?.errors[0]?.message.includes('No Session')
  ) {
    console.warn('[GraphQL] Redirecting to logout - No Session');
    //Redirect to process logout and then to start page
    window.location.href = '/logout?noSession=true';
  }

  //Handle session mismatch from single-window policy
  if (
    response?.response?.errors &&
    response?.response?.errors[0]?.message &&
    response?.response?.errors[0]?.message.includes('Session mismatch')
  ) {
    console.warn('[GraphQL] Redirecting to logout - Session mismatch');
    // Redirect to process logout and then to start page with specific message
    window.location.href = '/logout?sessionMismatch=true';
  }
};

type Headers = {
  [key: string]: string;
};

let currentHeaders: Headers = {};

const requestMiddleware: RequestMiddleware = async (request) => {
  const state: RootState = store.getState();
  const rememberMe = state.user?.rememberMe;
  const organizationId = state.organization?.organizationId;
  const activeLocale = state.locale?.activeLocale || 'en';
  let token = '';
  if (rememberMe) {
    token = getTokenLocalStorage();
  } else {
    token = getTokenSessionStorage();
  }
  const sessionUuid = getSessionUuid();

  const headers: Record<string, string> = {
    ...(request.headers as Record<string, string>),
    ...currentHeaders,
    Authorization: 'Bearer ' + token,
    'Session-Uuid': sessionUuid,
    'Accept-Language': activeLocale,
  };

  // Add organization header if available (skip for public/login requests)
  // Only add from Redux if not already set in currentHeaders (for org switching)
  if (organizationId && !request.url?.includes('publicSignup') && !currentHeaders['X-Organization-Id']) {
    headers['X-Organization-Id'] = organizationId;
  }

  return {
    ...request,
    headers,
  };
};

export { gql } from 'graphql-request';

export const graphQlClient = new GraphQLClient(process.env.REACT_APP_BACKEND_URL + 'graphql', {
  fetch,
  requestMiddleware,
  responseMiddleware,
});

// we might need to migrate to a different graphql library so that we don't need to use this separately
// and that the mutation requests are more descriptive. This will eventually reach a limitation.
export const graphQlUpload = async (file: File, entity: string, query: string, variables?: {}) => {
  const MAX_FILE_SIZE_MB = 50;
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    throw new Error('File size exceeds the maximum allowed size');
  }
  const state: RootState = store.getState();
  const rememberMe = state.user?.rememberMe;
  const organizationId = state.organization?.organizationId;
  const activeLocale = state.locale?.activeLocale || 'en';
  let token = '';
  if (rememberMe) {
    token = getTokenLocalStorage();
  } else {
    token = getTokenSessionStorage();
  }

  const operations = {
    query,
    variables: {
      [entity]: {
        ...variables,
        file: null, // This will be replaced in the 'map' with the actual file location.
      },
    },
  };

  const map = {
    '0': [`variables.${entity}.file`],
  };

  const formData = new FormData();
  formData.append('operations', JSON.stringify(operations));
  formData.append('map', JSON.stringify(map));
  formData.append('0', file); // The file should be appended last.

  const headers: Record<string, string> = {
    'apollo-require-preflight': 'true',
    Authorization: 'Bearer ' + token,
    'Accept-Language': activeLocale,
  };

  if (organizationId) {
    headers['X-Organization-Id'] = organizationId;
  }

  const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'graphql', {
    method: 'POST',
    body: formData,
    headers,
  });

  // Handle 413 Payload Too Large error
  if (response.status === 413) {
    throw new Error('FILE_TOO_LARGE');
  }

  // Handle other HTTP errors
  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }

  const responseData = await response.json();

  // Handle GraphQL errors
  if (responseData.errors && responseData.errors.length > 0) {
    throw new Error(responseData.errors[0].message);
  }

  return responseData.data;
};

// This function is used to update the headers of the graphql client for the impersonation feature
export const updateHeaders = (newHeaders: Headers) => {
  currentHeaders = {
    ...newHeaders,
  };
};

export const getCurrentHeaders = (): Headers => {
  return { ...currentHeaders };
};
